import { lookupDraftSitePacket } from "./draft-preview-packets";
import { publicLocationHeaderName } from "./public-location-cookie";
import {
  restaurantPlatform,
  resolveLocationByHostOrNull,
  type PlatformLocation,
} from "./restaurant-platform";
import {
  TODO_OWNER_INPUT,
  type SitePacket,
  type SourcedText,
} from "./site-packet";

export const draftPreviewTenantHeaderName = "x-draft-preview-tenant";
export const draftPreviewFlagHeaderName = "x-draft-preview";

export const DRAFT_PREVIEW_ROBOTS = {
  index: false,
  follow: false,
  googleBot: {
    index: false,
    follow: false,
    noimageindex: true,
  },
} as const;

export const DRAFT_PREVIEW_ROBOTS_CONTENT = "noindex, nofollow";

const DRAFT_PREVIEW_BLOCKED_PATH_PREFIXES = [
  "/order",
  "/reservations",
  "/sms-offers",
  "/giveaway",
  "/cove-club",
  "/account",
  "/club",
  "/tip",
  "/redeem",
  "/resident",
] as const;

export type DraftPreviewResolution =
  | { kind: "none" }
  | { kind: "not_found" }
  | {
      kind: "resolved";
      slug: string;
      location: PlatformLocation;
      packet: SitePacket | null;
    };

export type DraftPreviewRequestInput = {
  host: string | null | undefined;
  requestUrl?: string;
  searchParams?: URLSearchParams | string | null;
  pathname?: string;
  env?: Partial<NodeJS.ProcessEnv>;
  packet?: SitePacket | null;
  draftTenant?: string | null;
};

function normalizeHost(input: string | null | undefined): string {
  if (!input) return "";

  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return "";

  try {
    const url = trimmed.includes("://")
      ? new URL(trimmed)
      : new URL(`https://${trimmed}`);
    return url.hostname.replace(/^www\./, "").replace(/^\[([^\]]+)\]$/, "$1");
  } catch {
    const authority = trimmed
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0];
    if (authority === "::1") return authority;
    if (authority?.startsWith("[")) {
      return authority.slice(1, authority.indexOf("]"));
    }
    return authority?.split(":")[0] ?? "";
  }
}

function normalizePathname(pathname: string | undefined): string {
  const normalized = pathname?.split("?")[0]?.split("#")[0]?.replace(/\/+$/, "") ?? "";
  return normalized || "/";
}

function parseSearchParams(
  requestUrl: string | undefined,
  searchParams: URLSearchParams | string | null | undefined,
): URLSearchParams {
  if (searchParams instanceof URLSearchParams) return searchParams;
  if (typeof searchParams === "string") {
    return new URLSearchParams(
      searchParams.startsWith("?") ? searchParams.slice(1) : searchParams,
    );
  }
  if (requestUrl) {
    try {
      return new URL(requestUrl).searchParams;
    } catch {
      return new URLSearchParams();
    }
  }
  return new URLSearchParams();
}

function sanitizeTenantSlug(value: string | null | undefined): string | null {
  if (!value) return null;
  const slug = value.trim().toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return null;
  return slug;
}

function isRegisteredLocationDomain(host: string): boolean {
  const matched = resolveLocationByHostOrNull(host);
  return Boolean(
    matched?.domains.some((domain) => normalizeHost(domain) === host),
  );
}

export function isEphemeralPreviewHost(host: string | null | undefined): boolean {
  const normalized = normalizeHost(host);
  if (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1"
  ) {
    return true;
  }

  if (!normalized.endsWith(".vercel.app")) {
    return false;
  }

  return !isRegisteredLocationDomain(normalized);
}

export function isRegisteredProductionPublicHost(
  host: string | null | undefined,
): boolean {
  const normalized = normalizeHost(host);
  if (!normalized || isEphemeralPreviewHost(normalized)) {
    return false;
  }

  return isRegisteredLocationDomain(normalized);
}

export function isProductionDraftPreviewContext(
  host: string | null | undefined,
  env: Partial<NodeJS.ProcessEnv> = process.env,
): boolean {
  if (env.VERCEL_ENV === "production") return true;
  return isRegisteredProductionPublicHost(host);
}

export function extractDraftPreviewSlug(input: DraftPreviewRequestInput): string | null {
  const params = parseSearchParams(input.requestUrl, input.searchParams);
  const querySlug = sanitizeTenantSlug(params.get("tenant"));
  if (querySlug) return querySlug;

  const headerSlug = sanitizeTenantSlug(input.draftTenant);
  if (headerSlug) return headerSlug;

  const normalized = normalizeHost(input.host);
  const labels = normalized.split(".");
  if (labels.length >= 3 && labels[1] === "preview") {
    return sanitizeTenantSlug(labels[0] ?? "");
  }

  return null;
}

export function isLiveRegisteredTenantSlug(slug: string): boolean {
  return restaurantPlatform.locations.some(
    (location) =>
      location.restaurantId === slug ||
      location.slug === slug ||
      location.id === slug ||
      location.tenantId === slug ||
      location.businessId === slug,
  );
}

function sourcedTextValue(value: SourcedText, fallback = ""): string {
  if (value === TODO_OWNER_INPUT) return fallback;
  return value.value;
}

const WEEKDAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
} as const;

export function locationFromDraftPacket(packet: SitePacket): PlatformLocation {
  const place = packet.locations[0];
  const slug = packet.tenant.slug;
  const name = sourcedTextValue(packet.brand.name, packet.tenant.displayName);
  const hero =
    packet.images.find((image) => image.slot === "hero")?.path ??
    packet.images[0]?.path ??
    "";
  const weekly =
    place?.hours.map((hours) => ({
      days: [WEEKDAY_LABELS[hours.weekday]],
      opens: hours.opens,
      closes: hours.closes,
    })) ?? [];

  return {
    id: slug,
    slug,
    restaurantId: slug,
    tenantId: slug,
    businessId: slug,
    ownerGroupId: "draft-preview",
    name,
    legalName: name,
    domain: `${slug}.preview.local`,
    domains: [`${slug}.preview.local`],
    defaultBaseUrl: `https://${slug}.preview.local`,
    secondaryDomain: `${slug}.preview.local`,
    contact: {
      phone: place ? sourcedTextValue(place.phone) : "",
      email: "",
    },
    address: {
      line1: place ? sourcedTextValue(place.streetAddress) : "",
      city: place ? sourcedTextValue(place.city) : "",
      state: place ? sourcedTextValue(place.region) : "",
      zip: place ? sourcedTextValue(place.postalCode) : "",
    },
    reservationUrl: "/contact",
    openTableUrl: "",
    yelpReservationUrl: "",
    yelpReservationWidgetUrl: "",
    menuUrl: "/menu",
    orderUrl: "/contact",
    ordering: {
      mode: "external",
      orderUrl: "/contact",
      pickupEnabled: false,
      deliveryEnabled: false,
    },
    jobsUrl: "/jobs",
    socials: {
      instagram: "",
      facebook: "",
      tiktok: "",
      x: "",
      youtube: "",
      linkedin: "",
      pinterest: "",
      googleBusinessProfile: "",
    },
    ogImage: hero,
    ogImageAlt: `${name} draft preview`,
    heroImage: hero,
    heroVideo: "",
    logoImage: hero,
    twitterHandle: "",
    geo: {
      latitude: 0,
      longitude: 0,
    },
    hours: {
      open: weekly[0]?.opens ?? "",
      close: weekly[0]?.closes ?? "",
      weekly,
    },
    rating: {
      value: "",
      count: "",
    },
    operator: {
      name: "",
      jobTitle: "",
      sameAs: [],
    },
    source: {
      url: packet.sources[0]?.url ?? "draft-preview://packet",
      scrapedAt: packet.sources[0]?.fetchedAt ?? "",
    },
  };
}

export function resolveDraftPreviewRequest(
  input: DraftPreviewRequestInput,
): DraftPreviewResolution {
  const slug = extractDraftPreviewSlug(input);
  if (!slug) return { kind: "none" };

  if (isProductionDraftPreviewContext(input.host, input.env)) {
    return { kind: "not_found" };
  }

  if (isLiveRegisteredTenantSlug(slug)) {
    return { kind: "not_found" };
  }

  const packet = input.packet ?? lookupDraftSitePacket(slug);
  if (!packet || !packet.tenant.draft || packet.tenant.slug !== slug) {
    return { kind: "not_found" };
  }

  return {
    kind: "resolved",
    slug,
    location: locationFromDraftPacket(packet),
    packet,
  };
}

export function resolveDraftPreviewLocation(
  slug: string,
): PlatformLocation | null {
  const packet = lookupDraftSitePacket(slug);
  if (!packet) return null;
  return locationFromDraftPacket(packet);
}

/** Strip spoofed draft headers, then set them only for a resolved preview. */
export function applyDraftPreviewRequestHeaders(
  requestHeaders: Headers,
  resolution: DraftPreviewResolution,
): Headers {
  requestHeaders.delete(draftPreviewFlagHeaderName);
  requestHeaders.delete(draftPreviewTenantHeaderName);

  if (resolution.kind !== "resolved") {
    return requestHeaders;
  }

  requestHeaders.set(publicLocationHeaderName, resolution.location.id);
  requestHeaders.set(draftPreviewFlagHeaderName, "1");
  requestHeaders.set(draftPreviewTenantHeaderName, resolution.slug);
  return requestHeaders;
}

export function isDraftPreviewBlockedPath(pathname: string | undefined): boolean {
  const normalized = normalizePathname(pathname);
  if (
    DRAFT_PREVIEW_BLOCKED_PATH_PREFIXES.some(
      (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
    )
  ) {
    return true;
  }

  if (normalized.includes("/tickets")) return true;
  if (normalized.startsWith("/api/platform/")) return false;
  if (normalized.startsWith("/api/")) return true;
  return false;
}

export function draftPreviewDocumentPolicy() {
  return {
    robots: DRAFT_PREVIEW_ROBOTS_CONTENT,
    ordering: false,
    reservations: false,
    forms: false,
    stripe: false,
    smsConsent: false,
  } as const;
}

export function buildDraftPreviewRobotsTxt(): string {
  return ["User-agent: *", "Disallow: /", ""].join("\n");
}

export function renderDraftPreviewDocument(args: {
  title: string;
  bodyHtml: string;
}): string {
  const policy = draftPreviewDocumentPolicy();
  return [
    "<!DOCTYPE html>",
    "<html lang=\"en\">",
    "<head>",
    `  <meta name="robots" content="${policy.robots}">`,
    `  <title>${args.title}</title>`,
    "</head>",
    "<body>",
    `  <main>${args.bodyHtml}</main>`,
    "</body>",
    "</html>",
  ].join("\n");
}

export function findDraftPreviewHtmlViolations(html: string): string[] {
  const violations: string[] = [];
  if (!/noindex/i.test(html) || !/nofollow/i.test(html)) {
    violations.push("robots");
  }
  if (/<form[\s>]/i.test(html)) {
    violations.push("form");
  }
  if (/js\.stripe\.com|cdn\.stripe\.com|stripe\.com\/v3/i.test(html)) {
    violations.push("stripe");
  }
  if (
    /sms consent|text me offers|text us\b|by providing your (mobile )?phone/i.test(
      html,
    )
  ) {
    violations.push("sms");
  }
  return violations;
}

export { isDraftPreviewCommerceLinkKind } from "./draft-preview-chrome";
